import { useQuery } from "@tanstack/react-query";
import { compact, filter, reject } from "lodash/fp";

import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import type { ListTopicReportsParams, Topic } from "@app/analytics/types";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import type { User } from "@app/common/types";

const EMPTY_TOPIC_REPORTS: Topic[] = [];
const TWO_MINUTES = 2 * 60 * 1000;

const SAMPLE_READOUT_IDS = ["SAMPLE_TOPIC", "SAMPLE_TOPIC_SLACK"];
const matchSampleReports = (topic: Topic) =>
  SAMPLE_READOUT_IDS.includes(topic.id);

export const getQueryKey = (
  userId?: User["id"],
  params?: ListTopicReportsParams
) => {
  if (params) {
    return [
      "topics",
      userId,
      params?.dateRange?.startDate.toString(),
      params?.dateRange?.endDate.toString(),
      params?.view,
    ];
  }

  return compact(["topics", userId]);
};

const useTopicReports = (params: ListTopicReportsParams) => {
  const { userId } = useRollupContext();
  const { user } = useAuth();

  const topicsQuery = useQuery({
    queryKey: getQueryKey(user?.id, params),
    queryFn: () => apiClient.topics.listTopics(params),
    enabled: Boolean(user?.id) && userId === user?.id,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: TWO_MINUTES, // consistent with the refetch interval for meeting reports
  });

  // we don't currently support viewing anyone's topic reports but your own
  const topics =
    userId === user?.id
      ? topicsQuery.data ?? EMPTY_TOPIC_REPORTS
      : EMPTY_TOPIC_REPORTS;

  const sampleTopics = filter(matchSampleReports, topics);

  return {
    topics: params?.filterOutSampleReport
      ? reject(matchSampleReports, topics)
      : topics,
    sampleTopics,
    isLoading: topicsQuery.isLoading,
    isError: topicsQuery.isError,
  };
};

export default useTopicReports;
